import {QRCodeSVG} from 'qrcode.react';
import type {RequirementOf} from '../../lib/types';
import {DEFAULT_PAGE_NUMBER_FONT_SIZE} from '../../lib/constants';
import type {ResolvedPageNumber} from '../../lib/page-numbers';
import {effectivePositionMode} from '../../lib/position-mode';
import {substituteVars} from '../InlineLabel';
import {type ElemProps} from './element-props';
import {buildTextStyle} from './element-styles';

/**
 * The elements that draw no control: a rule, a QR code, a page number.
 *
 * Nothing here is fillable, nothing here reads the fill surface, and none of
 * them is more than a few dozen lines — so they sit together rather than each
 * holding a file open on its own.
 */

export function DividerRenderer({item}: ElemProps & {item: RequirementOf<'divider'>}) {
  const dCfg = item.config;
  return (
    <hr
      style={{
        borderStyle: dCfg.line_style ?? 'solid',
        borderColor: dCfg.color ?? '#d1d5db',
        borderTopWidth: dCfg.thickness ?? 1,
        borderBottomWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
      }}
    />
  );
}

export function QrRenderer({
  item,
  sampleData,
}: {
  item: RequirementOf<'qr_code'>;
  sampleData: Record<string, string>;
}) {
  const cfg = item.config;
  const baseSize = cfg.size && cfg.size > 0 ? cfg.size : 96;
  // Flow: sized by config (no resize handles). Absolute: sized by the element's
  // box so the resize handles work — kept square via the smaller dimension.
  const isAbsolute = effectivePositionMode(item) === 'absolute';
  const size = isAbsolute
    ? Math.max(32, Math.floor(Math.min(item.width ?? baseSize, item.height ?? baseSize)))
    : baseSize;
  // Template variables resolve against sample data in fill/preview mode; in the
  // editor (no data) the raw token is encoded so the code still renders.
  const content = substituteVars(cfg.value ?? '', sampleData).trim();

  if (!content) {
    return (
      <div
        style={{width: size, height: size}}
        className="flex flex-col items-center justify-center gap-1 rounded border border-dashed border-gray-300 bg-gray-50 text-center text-[10px] text-gray-400"
      >
        <span className="font-medium">QR</span>
        <span className="px-1 leading-tight">Set content</span>
      </div>
    );
  }

  return (
    <div className="py-1" style={{opacity: item.style.opacity ?? undefined}}>
      <QRCodeSVG
        value={content}
        size={size}
        level={cfg.ecc_level ?? 'M'}
        marginSize={0}
        bgColor={item.style.background_color ?? '#ffffff'}
        fgColor={item.style.color ?? '#000000'}
      />
    </div>
  );
}

/**
 * The text of a page-number stamp. The running number is computed per page by
 * `stampsOnPage` and handed down, because the element itself has no idea which of
 * its repeated instances is being drawn. Placement and alignment are the band's job
 * (see `pageNumberBand`) — this renders the text and nothing else, which is why it
 * takes none of the shared `ElemProps` plumbing.
 *
 * `text: null` reaches here only in the editor, when the element's range starts past
 * the last page: `stampsOnPage` drops an out-of-range stamp everywhere else. The
 * placeholder keeps the band findable and selectable instead of leaving an element
 * that renders nowhere.
 */

export function PageNumberRenderer({
  item,
  pn,
  text,
}: {
  item: RequirementOf<'page_number'>;
  pn: ResolvedPageNumber;
  text: string | null;
}) {
  if (text === null) {
    return (
      <span
        data-testid="page-number-placeholder"
        className="italic text-gray-400"
        style={{fontSize: DEFAULT_PAGE_NUMBER_FONT_SIZE}}
      >
        starts on page {pn.show_from}
      </span>
    );
  }
  return (
    <span
      data-testid="page-number"
      className="whitespace-nowrap"
      style={buildTextStyle(item.style, {fontSize: `${DEFAULT_PAGE_NUMBER_FONT_SIZE}px`})}
    >
      {text}
    </span>
  );
}
